import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { ComboBoxControl } from "@/Common/Components/Inputs/ComboboxControl";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { SimpleTextareaResizable } from "@/Common/Components/Inputs/SimpleTextareaResizable";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { ClinicianSearchStrategy, PersonSearchAutocompleteCustom } from "@/Common/Components/Search/PersonSearchAutocompleteCustom";
import { CliniciansHelper } from "@/Common/Helpers/CliniciansHelper";
import { CommonHelper } from "@/Common/Helpers/CommonHelper";
import { DocumentTreeClassControl } from "@/Common/Helpers/DocumentTreeClassControl";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { DocumentClassModel } from "@/Common/Models/DocumentClassModel";
import { Guid } from "@/Common/Models/Guid";
import { Person } from "@/Common/Models/Person/Person";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { PersonDropdownItem } from "@/Common/Models/UI/Inputs/PersonDropdownItem";
import { DocumentClass } from "@/Common/Resources/Lookups/Enums/DocumentClasses";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { LockIconComponent } from "@/Documents/Components/InboundFax/LockIconComponent";
import { InboundFaxIndexingFormModel } from "@/Documents/Models/InboundFax/InboundFaxIndexingFormModel";
import { InboundFaxesMailboxActions } from "./InboundFaxesMailboxActions";

import "./InboundFaxIndexingForm.css";

interface InboundFaxIndexingFormProps {
    defaultDocumentDate: Date;
    onChange: (formData: InboundFaxIndexingFormModel) => void;
    inboundFaxIndexingFormModel: InboundFaxIndexingFormModel,
    dateReceived: Date;
    faxNumber: string;
    onAssignSourceToFaxNumberChange: (newValue: boolean) => void;
    onAssignClassToFaxNumberChange: (newValue: boolean) => void;
    showReturnFaxToSenderPopup: () => void;
    isPatientFieldLocked: boolean;
    isDescriptionFieldLocked: boolean;
    isProvidersFieldLocked: boolean;
    isSiteFieldLocked: boolean;
    selectedPages: number[]
    onTogglePatientFieldLock: (isLocked: boolean) => void;
    onToggleDescriptionFieldLock: (isLocked: boolean) => void;
    onToggleProvidersFieldLock: (isLocked: boolean) => void;
    onToggleSiteFieldLock: (isLocked: boolean) => void;
    onShowCreatePatientPopup: (showPopUp: boolean) => void;
    reportClassLookupId: string;
    reportSubClassLookupId: string;
    descriptionsAutoCompleteList: Immutable.List<SearchableItemModelT<string>>;
    sourcesAutoCompleteList: Immutable.List<SearchableItemModelT<string>>;
    faxSourceClassLookupId: string;
    onRemoveFaxSourcesClass: (senderFaxNumber: string) => void;
}

class InboundFaxIndexingForm extends BaseReactComponent<InboundFaxIndexingFormProps> {
    private cliniciansSearchStrategy: ClinicianSearchStrategy;
    private classes: DocumentClassModel[];
    private selectedClass: { classLookupId: Guid | string; subClassLookupId: Guid | string; };
    private responsibleProvidersAutofillRequiredClasses: (Guid | string)[] = this.getClassLookupID([DocumentClass.Results, DocumentClass.MedicationAndPrescriptions, DocumentClass.ConsultationReports]);

    public constructor(props: InboundFaxIndexingFormProps) {
        super(props);
        this.cliniciansSearchStrategy = CliniciansHelper.GetAllCliniciansSearchStrategy();
        this.classes = DocumentTreeClassControl.GetDocumentClasses().toArray();
    }

    public componentDidUpdate(prevProps: Readonly<InboundFaxIndexingFormProps>): void {
        if (this.props.inboundFaxIndexingFormModel?.selectedPatient?.ContactFacetId !== prevProps.inboundFaxIndexingFormModel?.selectedPatient?.ContactFacetId) {
            if (this.props.inboundFaxIndexingFormModel?.selectedPatient) {
                this.changeClinicianSearchStrategy(this.props.inboundFaxIndexingFormModel.selectedPatient);
            }
            else {
                this.cliniciansSearchStrategy = CliniciansHelper.GetAllCliniciansSearchStrategy();
            }
        }
    }

    public render(): JSX.Element {
        const initialClassLookupID = this.props.inboundFaxIndexingFormModel?.selectedClassLookupId;
        const initialSubClassLookupID = this.props.inboundFaxIndexingFormModel?.selectedSubClassLookupId;
        const selectedClassID = this.getClassID(initialClassLookupID?.toString(), initialSubClassLookupID?.toString());
        const selectedPatient = [];

        this.selectedClass = {
            classLookupId: initialClassLookupID,
            subClassLookupId: initialSubClassLookupID
        };

        if (this.props.inboundFaxIndexingFormModel?.selectedPatient != null) {
            selectedPatient.push(this.props.inboundFaxIndexingFormModel?.selectedPatient);
        }

        return (
            <div id="inbound-fax-indexing-form">
                <div className="inbound-fax-form-row">
                    <label htmlFor="faxIndexingFormPatient">
                        {cacheStore.GetString("PatientLabel")}
                    </label>

                    <PersonSearchAutocompleteCustom
                        componentId="faxIndexingFormPatient"
                        dataCy="fax-indexing-patient"
                        valid={this.props.inboundFaxIndexingFormModel.selectedPatient != null}
                        useBlurEvent={false}
                        focusOnLoad={true}
                        selectedItemList={Immutable.List(selectedPatient)}
                        closeDropDownAfterSelection={true}
                        allowMultipleItemSelection={false}
                        onSelectionCallback={async (personItem: PersonDropdownItem) => await this.onSelectPatient(personItem.Item)}
                        onClearCallback={async () => {
                            this.props.onChange({ ...this.props.inboundFaxIndexingFormModel, selectedPatient: null, selectedResponsibleProviders: null });
                            this.cliniciansSearchStrategy = await CliniciansHelper.GetAllCliniciansSearchStrategy();
                        }}
                    />

                    <LockIconComponent
                        isLocked={this.props.isPatientFieldLocked}
                        onChange={isLocked => this.props.onTogglePatientFieldLock(isLocked)}
                    />

                    {!this.props.inboundFaxIndexingFormModel.selectedPatient && cacheStore?.ClinicSettings.EnablePatientEdit && (
                        <a
                            className="right inboundFax-label-no-bold"
                            onClick={() => this.props.onShowCreatePatientPopup(true)}
                        >
                            {cacheStore.GetString("CreatePatientFileLabel")}
                        </a>
                    )}
                </div>

                <div className="inbound-fax-form-row">
                    <label htmlFor="inbound_fax_classField">
                        {cacheStore.GetString("ClassLabel")}
                    </label>

                    <div className="inbound-fax-class-control-container">
                        <select
                            className="form-control myle-control"
                            data-cy="fax-indexing-class"
                            onChange={e => this.handleOnClassChanged(e)}
                            value={selectedClassID}
                        >
                            {this.classes?.map(g => (
                                <optgroup key={g.LookupId} label={cacheStore.GetLocalizedValueByLookupId(g.LookupId)}>
                                    {g.SubClasses.toArray().map(s => (
                                        <option
                                            key={s.LookupId}
                                            value={this.getClassID(g?.LookupId, s?.LookupId)}
                                        >
                                            {cacheStore.GetLocalizedValueByLookupId(g.LookupId) + " - " + cacheStore.GetLocalizedValueByLookupId(s.LookupId)}
                                        </option>
                                    ))}
                                </optgroup>
                            ))}
                        </select>

                        <div id="inbound-fax-assign-container">
                            {this.isTheSelectedClassEqualToTheCurrentFaxesClass() ?
                                (
                                    <a
                                        className="inboundFax-label-no-bold"
                                        onClick={() => this.dissociateClassFromFaxNumber()}
                                    >
                                        {cacheStore.GetString("DissociateClassFromFaxNumberLabel")}
                                    </a>
                                ) :
                                (
                                    <CheckboxControl
                                        label={cacheStore.GetString("InboundFax_AssociateClassToFaxLabel")}
                                        checked={this.props.inboundFaxIndexingFormModel?.shouldAssignClassFaxNumber}
                                        onChange={(isChecked: boolean) => this.props.onAssignClassToFaxNumberChange(isChecked)}
                                        labelClassName="inboundFax-label-no-bold"
                                    />
                                )
                            }
                        </div>
                    </div>
                </div>

                <div className="inbound-fax-form-row">
                    <label htmlFor="faxIndexingFormDescription">
                        {cacheStore.GetString("DescriptionLabel")}
                    </label>

                    <TextboxControl
                        id="faxIndexingFormDescription"
                        dataCy="fax-indexing-description"
                        value={this.props.inboundFaxIndexingFormModel?.description ?? ""}
                        onChange={text => this.props.onChange({ ...this.props.inboundFaxIndexingFormModel, description: text })}
                        className="form-control description-input-height"
                        autoCompleteItems={this.props.descriptionsAutoCompleteList}
                        onAutoCompleteSelectCallBack={(code, model, value) => this.props.onChange({ ...this.props.inboundFaxIndexingFormModel, description: value })}
                        RemoveStyle
                    />

                    <LockIconComponent
                        isLocked={this.props.isDescriptionFieldLocked}
                        onChange={isLocked => this.props.onToggleDescriptionFieldLock(isLocked)}
                    />
                </div>

                <div className="inbound-fax-form-row">
                    <label htmlFor="faxIndexingFormResponsibleProvider">
                        {cacheStore.GetString("ResponsibleProvidersLabel")}
                    </label>

                    <PersonSearchAutocompleteCustom
                        componentId="faxIndexingFormResponsibleProvider"
                        dataCy="fax-indexing-provider"
                        selectedItemList={Immutable.List(this.props?.inboundFaxIndexingFormModel?.selectedResponsibleProviders ?? [])}
                        focusOnLoad={false}
                        closeDropDownAfterSelection={false}
                        searchStrategy={this.cliniciansSearchStrategy}
                        allowMultipleItemSelection={true}
                        canCloseDropdownAfterAllItemsSelected={true}
                        onSearch={(searchText: string) => {
                            if (IsNullOrWhiteSpace(searchText) &&
                                !CommonHelper.hasAllPerson(
                                    this.cliniciansSearchStrategy.cliniciansToSearchFrom,
                                    this.props.inboundFaxIndexingFormModel.selectedResponsibleProviders
                                )
                            ) {
                                return this.cliniciansSearchStrategy.executeSearch(null);
                            }
                            else {
                                return CliniciansHelper.GetAllCliniciansSearchStrategy().executeSearch(searchText);
                            }
                        }}
                        onClearCallback={(personItem: PersonDropdownItem) => {
                            const selectedResponsibleProviders = this.props.inboundFaxIndexingFormModel.selectedResponsibleProviders.filter(x => x.ContactId !== personItem.Item.ContactId);
                            this.props.onChange({ ...this.props.inboundFaxIndexingFormModel, selectedResponsibleProviders });
                        }}
                        onSelectionCallback={(personItem: PersonDropdownItem) => {
                            this.props.onChange({
                                ...this.props.inboundFaxIndexingFormModel,
                                selectedResponsibleProviders: this.onSelectProvider(personItem.Item)
                            });
                        }}
                    />

                    <LockIconComponent
                        isLocked={this.props.isProvidersFieldLocked}
                        onChange={isLocked => this.props.onToggleProvidersFieldLock(isLocked)}
                    />
                </div>

                <div className="inbound-fax-form-row">
                    <label htmlFor="inboundFaxSiteField">
                        {cacheStore.GetString("SiteDLabel")}
                    </label>

                    <ComboBoxControl
                        id="inboundFaxSiteField"
                        dataCy="fax-indexing-site"
                        selectedCode={this.props.inboundFaxIndexingFormModel?.selectedSiteCode}
                        items={Immutable.List(cacheStore.SiteCache.map(x => new ComboBoxDropDownItem(x.Model.Id, x.Model.ClinicalName, 0)))}
                        hasEmptyItem={false}
                        onChange={selectedCode => this.props.onChange({ ...this.props.inboundFaxIndexingFormModel, selectedSiteCode: selectedCode })}
                    />

                    <LockIconComponent
                        isLocked={this.props.isSiteFieldLocked}
                        onChange={isLocked => this.props.onToggleSiteFieldLock(isLocked)}
                    />
                </div>

                <div className="inbound-fax-form-row">
                    <label htmlFor="faxIndexingFormSource">
                        {cacheStore.GetString("SourceLabel")}
                    </label>

                    <TextboxControl
                        id="faxIndexingFormSource"
                        value={this.props.inboundFaxIndexingFormModel?.source ?? ""}
                        onChange={text => this.props.onChange({ ...this.props.inboundFaxIndexingFormModel, source: text })}
                        className="form-control source-input-height"
                        autoCompleteItems={this.props.sourcesAutoCompleteList}
                        onAutoCompleteSelectCallBack={(code, model, value) => this.props.onChange({ ...this.props.inboundFaxIndexingFormModel, source: value })}
                        RemoveStyle
                    />
                    <CheckboxControl
                        label={cacheStore.GetString("AssignSourceToFaxNumberLabel")}
                        checked={this.props.inboundFaxIndexingFormModel?.shouldAssignSourceFaxNumber}
                        onChange={(isChecked: boolean) => this.props.onAssignSourceToFaxNumberChange(isChecked)}
                        labelClassName="inboundFax-label-no-bold"
                    />
                </div>

                <div className="inbound-fax-form-row">
                    <label htmlFor="inbound_fax_document_date">
                        {cacheStore.GetString("DocumentDateLabel")}
                    </label>

                    <DateSelector
                        dateFormat="dddd, dd MMMM yyyy"
                        id="inbound_fax_document_date"
                        cssClass="inbound-fax-document-date"
                        date={this.props.inboundFaxIndexingFormModel?.documentDate}
                        onChangeCallback={date => this.props.onChange({ ...this.props.inboundFaxIndexingFormModel, documentDate: date })}
                    />

                    <a
                        onClick={() => this.props.onChange({ ...this.props.inboundFaxIndexingFormModel, documentDate: new Date() })}
                        className="today-link"
                    >
                        {cacheStore.GetString("TodayLabel")}
                    </a>

                    <label className="inboundFax-label-no-bold">
                        <b>{cacheStore.GetString("DateReceivedLabel")}</b>&nbsp;&nbsp;{this.getFormattedDateReceived()} &emsp;
                        <b>{cacheStore.GetString("FaxNumberLabel")}</b>&nbsp;&nbsp;
                        {this.props.faxNumber && this.props.faxNumber.trim() !== ""
                            ? MyleHelper.FormatPhone(MyleHelper.RemoveCountryCodeCanadianE164PhoneNumber(this.props.faxNumber), "CNCANADA")
                            : null
                        }
                    </label>
                </div>
                <div className="inbound-fax-form-row">
                    <label htmlFor="returnFaxToSenderPopup_NotesField">
                        {cacheStore.GetString("NotesLabel")}
                    </label>

                    <SimpleTextareaResizable
                        id="returnFaxToSenderPopup_NotesField"
                        className="form-control textarea-resize-vertical"
                        rows={3}
                        value={this.props.inboundFaxIndexingFormModel?.notes ?? ""}
                        onChange={event => this.props.onChange({ ...this.props.inboundFaxIndexingFormModel, notes: event.target.value })}
                        maxLength={1000}
                        minHeight="90px"
                    />

                    {!cacheStore.ClinicSettings.EnableFaxCoverSheet && <a className="returnToSenderLink inboundFax-label-no-bold" onClick={() => this.props.showReturnFaxToSenderPopup()}>{cacheStore.GetString("ReturnToSenderLabel")}</a>}
                </div>
            </div>
        );
    }

    private getFormattedDateReceived(): string {
        return LuxonDateTimeParser
            .parse(this.props.dateReceived)
            .toLocal()
            .toFormat(`yyyy/LL/dd '${cacheStore.GetString("AtLabel")}' HH:mm`);
    }

    private async handleOnClassChanged(e: React.ChangeEvent<HTMLSelectElement>): Promise<void> {
        this.selectedClass = JSON.parse(e.target.value);

        this.props.onChange({
            ...this.props.inboundFaxIndexingFormModel,
            selectedClassLookupId: this.selectedClass.classLookupId,
            selectedSubClassLookupId: this.selectedClass.subClassLookupId
        });

        const selectedPatient = this.props.inboundFaxIndexingFormModel.selectedPatient;
        if (selectedPatient != null) {
            await this.changeClinicianSearchStrategy(selectedPatient);
            await this.autopopulateResponsibleProvider(selectedPatient, this.selectedClass.classLookupId);
        }
    }

    private async changeClinicianSearchStrategy(patient: Person): Promise<void> {
        this.cliniciansSearchStrategy = await CliniciansHelper.getSortedPatientClinicianSearchStrategy(patient);
    }

    private async autopopulateResponsibleProvider(patient: Person, selectedClassName: Guid | string): Promise<void> {
        if (!IsNullOrUndefined(patient.ContactId) && this.responsibleProvidersAutofillRequiredClasses.includes(selectedClassName)) {
            const selectedProviders: Person[] = [...(this.props.inboundFaxIndexingFormModel?.selectedResponsibleProviders || [])];
            const responsibleProviderToAutopopulate: Person = await CliniciansHelper.getStarredAndRamqRegisteredClinicians(patient);
            const isClinicianAlreadySelected = selectedProviders.some(p => p.ContactFacetId === responsibleProviderToAutopopulate?.ContactFacetId);

            if (responsibleProviderToAutopopulate && !isClinicianAlreadySelected) {
                this.props.onChange({
                    ...this.props.inboundFaxIndexingFormModel,
                    selectedResponsibleProviders: [...selectedProviders, responsibleProviderToAutopopulate]
                });
            }
        }
    }

    private onSelectProvider(personItem: Person): Person[] {
        return (this.props.inboundFaxIndexingFormModel?.selectedResponsibleProviders?.any(x => x.ContactId === personItem.ContactId))
            ? this.props.inboundFaxIndexingFormModel.selectedResponsibleProviders.filter(x => x.ContactId !== personItem.ContactId)
            : [...(this.props.inboundFaxIndexingFormModel?.selectedResponsibleProviders || []), personItem];
    }

    private getClassLookupID(docClassLookupCodes: string[]): (Guid | string)[] {
        return docClassLookupCodes.map(e => DocumentClassModel.FromLookup(cacheStore.FindLookupByLookupCode(e)).LookupId);
    }

    private async onSelectPatient(patient: Person): Promise<void> {
        this.props.onChange({ ...this.props.inboundFaxIndexingFormModel, selectedPatient: patient });
        await this.changeClinicianSearchStrategy(patient);
        await this.autopopulateResponsibleProvider(patient, this.selectedClass.classLookupId);
    }

    private getClassID(classLookupId: string, subClassLookupId: string): string {
        return JSON.stringify(
            {
                classLookupId: classLookupId?.toLowerCase() ?? "",
                subClassLookupId: subClassLookupId?.toLowerCase() ?? ""
            }
        );
    }

    private isTheSelectedClassEqualToTheCurrentFaxesClass(): boolean {
        if (!this.props.faxSourceClassLookupId || !this.props.inboundFaxIndexingFormModel.selectedClassLookupId) {
            return false;
        }

        return this.props.faxSourceClassLookupId === this.props.inboundFaxIndexingFormModel.selectedClassLookupId;
    }

    private async dissociateClassFromFaxNumber(): Promise<void> {
        const isClassDissociatedFromInboundFax = await InboundFaxesMailboxActions.dissociateClassFromFaxNumber(this.props.faxNumber);

        if (!isClassDissociatedFromInboundFax) {
            return;
        }

        this.props.onRemoveFaxSourcesClass(this.props.faxNumber);
    }
}

export { InboundFaxIndexingForm };
